import { useState } from 'react';
import { useDisclosure } from '@mantine/hooks';
import {
  IconSearch,
  IconDotsVertical,
  IconEye,
  IconTruck,
  IconX,
  IconCheck,
  IconReceipt,
} from '@tabler/icons-react';
import { ActionIcon, Badge, Box, Button, Card, Divider, Group, Menu, Modal, Select, Stack, Table, Text, TextInput, Title } from '@/components/ui';
import { mockOrders, Order } from '../mockData/ecommerce';

const formatCurrency = (value: number) => {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
};

const formatDate = (date: Date) => {
  return new Intl.DateTimeFormat('en-US', { dateStyle: 'medium' }).format(date);
};

const getStatusColor = (status: Order['status']) => {
  switch (status) {
    case 'delivered': return 'green';
    case 'shipped': return 'cyan';
    case 'processing': return 'blue';
    case 'pending': return 'yellow';
    case 'cancelled': return 'red';
    case 'refunded': return 'orange';
    default: return 'gray';
  }
};

const getPaymentStatusColor = (status: Order['paymentStatus']) => {
  switch (status) {
    case 'paid': return 'green';
    case 'pending': return 'yellow';
    case 'failed': return 'red';
    case 'refunded': return 'orange';
    default: return 'gray';
  }
};

export default function OrdersPage() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [paymentFilter, setPaymentFilter] = useState<string | null>(null);
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [opened, { open, close }] = useDisclosure(false);

  const statuses = ['pending', 'processing', 'shipped', 'delivered', 'cancelled', 'refunded'];
  const paymentStatuses = ['paid', 'pending', 'failed', 'refunded'];

  const filteredOrders = mockOrders.filter(order => {
    const matchesSearch = order.orderNumber.toLowerCase().includes(search.toLowerCase()) ||
                          order.customerName.toLowerCase().includes(search.toLowerCase()) ||
                          order.customerEmail.toLowerCase().includes(search.toLowerCase());
    const matchesStatus = !statusFilter || order.status === statusFilter;
    const matchesPayment = !paymentFilter || order.paymentStatus === paymentFilter;
    return matchesSearch && matchesStatus && matchesPayment;
  });

  const handleViewOrder = (order: Order) => {
    setSelectedOrder(order);
    open();
  };

  return (
    <Stack gap="lg">
      {/* Header */}
      <Group justify="space-between">
        <Box>
          <Title order={2}>Orders</Title>
          <Text c="dimmed" size="sm">{filteredOrders.length} orders found</Text>
        </Box>
        <Button leftSection={<IconReceipt size={16} />}>Export Orders</Button>
      </Group>

      {/* Filters */}
      <Card>
        <Group>
          <TextInput
            placeholder="Search orders..."
            leftSection={<IconSearch size={16} />}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1 }}
          />
          <Select
            placeholder="Order Status"
            data={statuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable
            w={150}
          />
          <Select
            placeholder="Payment"
            data={paymentStatuses.map(s => ({ value: s, label: s.charAt(0).toUpperCase() + s.slice(1) }))}
            value={paymentFilter}
            onChange={setPaymentFilter}
            clearable
            w={130}
          />
        </Group>
      </Card>

      {/* Orders Table */}
      <Card p={0}>
        <Table striped highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Order</Table.Th>
              <Table.Th>Customer</Table.Th>
              <Table.Th>Date</Table.Th>
              <Table.Th>Items</Table.Th>
              <Table.Th>Status</Table.Th>
              <Table.Th>Payment</Table.Th>
              <Table.Th ta="right">Total</Table.Th>
              <Table.Th w={50}></Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {filteredOrders.map((order) => (
              <Table.Tr key={order.id}>
                <Table.Td>
                  <Text size="sm" fw={600}>{order.orderNumber}</Text>
                </Table.Td>
                <Table.Td>
                  <Box>
                    <Text size="sm" fw={500}>{order.customerName}</Text>
                    <Text size="xs" c="dimmed">{order.customerEmail}</Text>
                  </Box>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{formatDate(order.createdAt)}</Text>
                </Table.Td>
                <Table.Td>
                  <Text size="sm">{order.items.length} items</Text>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" color={getStatusColor(order.status)}>
                    {order.status}
                  </Badge>
                </Table.Td>
                <Table.Td>
                  <Badge size="sm" variant="outline" color={getPaymentStatusColor(order.paymentStatus)}>
                    {order.paymentStatus}
                  </Badge>
                </Table.Td>
                <Table.Td ta="right">
                  <Text size="sm" fw={600}>{formatCurrency(order.total)}</Text>
                </Table.Td>
                <Table.Td>
                  <Menu position="bottom-end">
                    <Menu.Target>
                      <ActionIcon variant="subtle" color="gray">
                        <IconDotsVertical size={16} />
                      </ActionIcon>
                    </Menu.Target>
                    <Menu.Dropdown>
                      <Menu.Item leftSection={<IconEye size={14} />} onClick={() => handleViewOrder(order)}>
                        View Details
                      </Menu.Item>
                      <Menu.Item leftSection={<IconTruck size={14} />}>Update Status</Menu.Item>
                      <Menu.Divider />
                      <Menu.Item leftSection={<IconX size={14} />} color="red">Cancel Order</Menu.Item>
                    </Menu.Dropdown>
                  </Menu>
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Card>

      {/* Order Details Modal */}
      <Modal opened={opened} onClose={close} title="Order Details" size="lg">
        {selectedOrder && (
          <Stack gap="md">
            <Group justify="space-between">
              <Box>
                <Text size="lg" fw={700}>{selectedOrder.orderNumber}</Text>
                <Text size="sm" c="dimmed">Placed on {formatDate(selectedOrder.createdAt)}</Text>
              </Box>
              <Group>
                <Badge size="lg" color={getStatusColor(selectedOrder.status)}>
                  {selectedOrder.status}
                </Badge>
                <Badge size="lg" variant="outline" color={getPaymentStatusColor(selectedOrder.paymentStatus)}>
                  {selectedOrder.paymentStatus}
                </Badge>
              </Group>
            </Group>

            <Divider />

            <Box>
              <Text fw={600} mb="xs">Customer Information</Text>
              <Text size="sm">{selectedOrder.customerName}</Text>
              <Text size="sm" c="dimmed">{selectedOrder.customerEmail}</Text>
              <Text size="sm" c="dimmed">{selectedOrder.shippingAddress}</Text>
            </Box>

            <Divider />

            <Box>
              <Text fw={600} mb="xs">Order Items</Text>
              <Table>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Product</Table.Th>
                    <Table.Th ta="center">Qty</Table.Th>
                    <Table.Th ta="right">Price</Table.Th>
                    <Table.Th ta="right">Total</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {selectedOrder.items.map((item) => (
                    <Table.Tr key={item.productId}>
                      <Table.Td>{item.productName}</Table.Td>
                      <Table.Td ta="center">{item.quantity}</Table.Td>
                      <Table.Td ta="right">{formatCurrency(item.price)}</Table.Td>
                      <Table.Td ta="right">{formatCurrency(item.price * item.quantity)}</Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Box>

            <Divider />

            <Stack gap="xs">
              <Group justify="space-between">
                <Text size="sm">Subtotal</Text>
                <Text size="sm">{formatCurrency(selectedOrder.subtotal)}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Tax</Text>
                <Text size="sm">{formatCurrency(selectedOrder.tax)}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm">Shipping</Text>
                <Text size="sm">{selectedOrder.shipping === 0 ? 'Free' : formatCurrency(selectedOrder.shipping)}</Text>
              </Group>
              <Divider />
              <Group justify="space-between">
                <Text fw={600}>Total</Text>
                <Text fw={700} size="lg">{formatCurrency(selectedOrder.total)}</Text>
              </Group>
            </Stack>

            <Group justify="flex-end" mt="md">
              <Button variant="light" onClick={close}>Close</Button>
              <Button leftSection={<IconCheck size={16} />}>Mark as Shipped</Button>
            </Group>
          </Stack>
        )}
      </Modal>
    </Stack>
  );
}
